/**
 * Interface languages (spec §8.23) `[M23]`.
 *
 * Three catalogues of the same flat key space, English the source. No translation library: a
 * context, a lookup, `{name}` interpolation and `Intl.PluralRules` are the whole mechanism, and the
 * platform already provides the last of them.
 *
 * The reader's choice lives on their account (`UserOut.language`, Q60) so it follows them between
 * devices; until they choose, the best match for `navigator.languages` is used and nothing is
 * stored — which is also why the Playwright suite, whose browsers ask for `en-US`, keeps seeing
 * English.
 */
import { createContext } from "react";
import de from "./de.json";
import en from "./en.json";
import es from "./es.json";

export const LANGUAGES = ["en", "de", "es"] as const;
export type Language = (typeof LANGUAGES)[number];

/** What the switcher shows: each language names itself. */
export const LANGUAGE_NAMES: Record<Language, string> = { en: "English", de: "Deutsch", es: "Español" };

const CATALOGUES: Record<Language, Record<string, unknown>> = { en, de, es };

export const isLanguage = (v: unknown): v is Language => typeof v === "string" && (LANGUAGES as readonly string[]).includes(v);

/** The best match for the browser's preferences, English when none matches (§8.23). */
export function browserLanguage(preferred: readonly string[] = navigator.languages ?? []): Language {
  for (const tag of preferred) {
    const base = tag.toLowerCase().split("-")[0];
    if (isLanguage(base)) return base;
  }
  return "en";
}

export type Params = Record<string, string | number>;
/** A plural value: the catalogue holds one message per CLDR category the language uses. */
export type Plural = Partial<Record<Intl.LDMLPluralRule, string>>;

/** `{name}` → `params.name`; an unknown placeholder is left alone so the gap is visible. */
export function interpolate(message: string, params?: Params): string {
  if (!params) return message;
  return message.replace(/\{(\w+)\}/g, (whole, name: string) => (name in params ? String(params[name]) : whole));
}

export function translate(language: Language, key: string, params?: Params): string {
  const own = CATALOGUES[language][key] ?? CATALOGUES.en[key];
  if (own === undefined) return key; // a missing key shows itself rather than an empty space
  if (typeof own === "string") return interpolate(own, params);
  // a plural object: the count decides the category, the language decides which categories exist
  const count = Number(params?.count ?? 0);
  const rule = new Intl.PluralRules(language).select(count);
  const plural = own as Plural;
  const message = plural[rule] ?? plural.other ?? Object.values(plural)[0] ?? key;
  return interpolate(message, params);
}

export interface I18n {
  language: Language;
  t: Translate;
}

/** The translate function, for the helpers outside React that a component hands it to. */
export type Translate = (key: string, params?: Params) => string;

export const I18nContext = createContext<I18n>({ language: "en", t: (key, params) => translate("en", key, params) });
